import { createRoute, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { useEffect, useState, FormEvent } from 'react';
import { useAuthStore } from '../../store';
import { auth, db } from '../../firebase';
import { Store, Package, Receipt, CreditCard, BarChart, MessageCircle, HelpCircle, BookOpen, ChevronDown, ChevronRight, Bell } from 'lucide-react';
import { ProductsManager } from '../../components/admin/ProductsManager';
import { TransactionHistory } from '../../components/admin/TransactionHistory';
import { VendorAnalytics } from '../../components/admin/VendorAnalytics';
import { Messaging } from '../../components/Messaging';
import { QAManager } from '../../components/admin/QAManager';
import { VendorTutorial } from '../../components/vendor/VendorTutorial';
import { VendorNotifications } from '../../components/vendor/VendorNotifications';
import { doc, updateDoc, setDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendor',
  component: VendorDashboard,
});

type Tab = 'tutorial' | 'products' | 'transactions' | 'analytics' | 'messages' | 'qa' | 'settings' | 'payment' | 'notifications';

function VendorDashboard() {
  const { profile, isAuthReady } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('tutorial');
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuCollapsed, setMenuCollapsed] = useState<Record<string, boolean>>({
    'getting-started': false,
    'store-management': false,
    'sales-operations': false,
    'communication': false,
    'configuration': false
  });
  const toggleMenu = (group: keyof typeof menuCollapsed) => {
    setMenuCollapsed(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const [storeName, setStoreName] = useState('');
  const [storeDescription, setStoreDescription] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupSchedules, setPickupSchedules] = useState<Record<string, { startTime: string; endTime: string; enabled: boolean }>>({
    '0': { enabled: false, startTime: '09:00', endTime: '17:00' },
    '1': { enabled: true, startTime: '09:00', endTime: '17:00' },
    '2': { enabled: true, startTime: '09:00', endTime: '17:00' },
    '3': { enabled: true, startTime: '09:00', endTime: '17:00' },
    '4': { enabled: true, startTime: '09:00', endTime: '17:00' },
    '5': { enabled: true, startTime: '09:00', endTime: '17:00' },
    '6': { enabled: false, startTime: '09:00', endTime: '17:00' },
  });
  const [bannerImage, setBannerImage] = useState('');
  const [bannerColors, setBannerColors] = useState<string[]>(['#f3f4f6', '#f3f4f6', '#f3f4f6']);
  const [bannerType, setBannerType] = useState<'image' | 'color' | 'gradient'>('color');
  const [gradientType, setGradientType] = useState('linear-tb');
  const [profilePicture, setProfilePicture] = useState('');
  const [themeColors, setThemeColors] = useState<string[]>(['#171717', '#171717', '#171717']);
  const [themeType, setThemeType] = useState<'solid' | 'gradient'>('solid');
  const [policies, setPolicies] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [socialLinks, setSocialLinks] = useState({ instagram: '', facebook: '', twitter: '', website: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    console.log('VendorDashboard check:', { isAuthReady, profile });
    if (isAuthReady && profile?.role !== 'vendor' && profile?.role !== 'admin') {
      console.log('Redirecting to / because role is not vendor or admin');
      navigate({ to: '/' });
    }
    if (profile) {
      setStoreName(profile.storeName || '');
      setStoreDescription(profile.storeDescription || '');
      setPickupLocation(profile.pickupLocation || '');
      setBannerImage(profile.bannerImage || '');
      setBannerColors(profile.bannerColors || ['#f3f4f6', '#f3f4f6', '#f3f4f6']);
      setBannerType(profile.bannerType || 'color');
      setGradientType(profile.gradientType || 'linear-tb');
      setProfilePicture(profile.profilePicture || '');
      setThemeColors(profile.themeColors || ['#171717', '#171717', '#171717']);
      setThemeType(profile.themeType || 'solid');
      setPolicies(profile.policies || '');
      setContactEmail(profile.contactEmail || '');
      if (profile.socialLinks) {
        setSocialLinks(profile.socialLinks);
      }
      if (profile.pickupSchedules) {
        setPickupSchedules(profile.pickupSchedules);
      } else if (profile.pickupDays) {
        // Migrate old format
        const newSchedules = { ...pickupSchedules };
        for (let i = 0; i < 7; i++) {
          newSchedules[i.toString()] = {
            enabled: profile.pickupDays.includes(i),
            startTime: profile.pickupStartTime || '09:00',
            endTime: profile.pickupEndTime || '17:00'
          };
        }
        setPickupSchedules(newSchedules);
      }
    }

    // Unread notifications listener
    let unsubscribeNotifs: () => void = () => {};
    if (profile?.uid) {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', profile.uid),
        where('read', '==', false)
      );
      unsubscribeNotifs = onSnapshot(q, (snapshot) => {
        setUnreadCount(snapshot.size);
      });
    }
    
    // Check for Stripe onboarding success
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_onboarded') === 'true') {
      if (auth.currentUser) {
        // Update local profile state to reflect onboarding
        updateDoc(doc(db, 'users', auth.currentUser.uid), {
          stripeOnboarded: true
        }).then(() => {
          setMessage('Stripe account connected successfully!');
          setActiveTab('settings');
          // Remove query param
          window.history.replaceState({}, document.title, window.location.pathname);
          // Refresh profile to get the updated stripeOnboarded status
          useAuthStore.getState().fetchProfile(auth.currentUser!.uid);
        }).catch(console.error);
      }
    }
  }, [profile, isAuthReady, navigate]);

  const handleSaveSettings = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!auth.currentUser) return;
    setSaving(true);
    setMessage('');
    try {
      const updateData = {
        storeName,
        storeDescription,
        pickupLocation,
        pickupSchedules,
        bannerImage,
        bannerColors,
        bannerType,
        gradientType,
        profilePicture,
        themeColors,
        themeType,
        policies,
        contactEmail,
        socialLinks
      };

      await updateDoc(doc(db, 'users', auth.currentUser.uid), updateData);
      
      // Also update public store profile
      await setDoc(doc(db, 'stores', auth.currentUser.uid), {
        ...updateData,
        city: profile?.city || ''
      }, { merge: true });

      setMessage('Settings saved successfully!');
    } catch (err) {
      console.error(err);
      setMessage('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/stripe/connect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to generate Stripe Connect link.');
      }
    } catch (err) {
      console.error(err);
      alert('Error connecting to Stripe.');
    }
  };

  const handleMockConnectStripe = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/mock-stripe/connect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        alert('Mock Stripe Connected successfully! You can now receive mock payments.');
        if (auth.currentUser) {
           useAuthStore.getState().fetchProfile(auth.currentUser.uid);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!isAuthReady || (profile?.role !== 'vendor' && profile?.role !== 'admin')) {
    return (
      <div className="flex-1 flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full py-12 px-4 sm:px-6 lg:px-8 text-neutral-900 dark:text-white">
      <Helmet>
        <title>Vendor Dashboard - Cardston Market</title>
        <meta name="description" content="Manage your store products, orders, analytics, and settings on Cardston Market." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex items-center justify-between gap-3 mb-8">
        <div className="flex items-center gap-3">
          <Store className="w-8 h-8 text-neutral-900 dark:text-white stroke-[1.5]" />
          <h1 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight">Vendor Dashboard</h1>
        </div>
        <button 
          onClick={() => setActiveTab('messages')}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Messaging Channel
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="w-full md:w-64 shrink-0">
          <nav className="flex flex-col space-y-4">
            
            {/* Getting Started */}
            <div>
              <button 
                onClick={() => toggleMenu('getting-started')}
                className="flex items-center justify-between w-full px-2 py-1 mb-1 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <span>Getting Started</span>
                {menuCollapsed['getting-started'] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {!menuCollapsed['getting-started'] && (
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={() => setActiveTab('tutorial')}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      activeTab === 'tutorial' 
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                    }`}
                  >
                    <BookOpen className="w-5 h-5" />
                    Tutorial & Guide
                  </button>
                </div>
              )}
            </div>

            {/* Store Management */}
            <div>
              <button 
                onClick={() => toggleMenu('store-management')}
                className="flex items-center justify-between w-full px-2 py-1 mb-1 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <span>Store Management</span>
                {menuCollapsed['store-management'] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {!menuCollapsed['store-management'] && (
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={() => setActiveTab('products')}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      activeTab === 'products' 
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                    }`}
                  >
                    <Package className="w-5 h-5" />
                    My Products
                  </button>
                  <button
                    onClick={() => setActiveTab('qa')}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      activeTab === 'qa' 
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                    }`}
                  >
                    <HelpCircle className="w-5 h-5" />
                    Product Q&A
                  </button>
                </div>
              )}
            </div>

            {/* Sales & Operations */}
            <div>
              <button 
                onClick={() => toggleMenu('sales-operations')}
                className="flex items-center justify-between w-full px-2 py-1 mb-1 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <span>Sales & Operations</span>
                {menuCollapsed['sales-operations'] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {!menuCollapsed['sales-operations'] && (
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      activeTab === 'transactions' 
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                    }`}
                  >
                    <Receipt className="w-5 h-5" />
                    My Orders
                  </button>
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      activeTab === 'analytics' 
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                    }`}
                  >
                    <BarChart className="w-5 h-5" />
                    Analytics
                  </button>
                </div>
              )}
            </div>

            {/* Communication */}
            <div>
              <button 
                onClick={() => toggleMenu('communication')}
                className="flex items-center justify-between w-full px-2 py-1 mb-1 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <span>Communication</span>
                 {menuCollapsed['communication'] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
               </button>
               {!menuCollapsed['communication'] && (
                 <div className="flex flex-col space-y-1">
                   <button
                     onClick={() => setActiveTab('notifications')}
                     className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                       activeTab === 'notifications' 
                         ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                         : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                     }`}
                   >
                     <div className="relative">
                       <Bell className="w-5 h-5" />
                       {unreadCount > 0 && (
                         <span className="absolute -top-1.5 -right-1.5 bg-indigo-600 text-white text-[10px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full border-2 border-white dark:border-neutral-900 px-0.5">
                           {unreadCount > 9 ? '9+' : unreadCount}
                         </span>
                       )}
                     </div>
                     Notifications
                   </button>
                   <button
                     onClick={() => setActiveTab('messages')}
                     className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                       activeTab === 'messages' 
                         ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                         : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                     }`}
                   >
                     <MessageCircle className="w-5 h-5" />
                     Messages
                   </button>
                 </div>
               )}
            </div>

            {/* Configuration */}
            <div>
              <button 
                onClick={() => toggleMenu('configuration')}
                className="flex items-center justify-between w-full px-2 py-1 mb-1 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <span>Configuration</span>
                 {menuCollapsed['configuration'] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
               </button>
               {!menuCollapsed['configuration'] && (
                 <div className="flex flex-col space-y-1">
                   <button
                     onClick={() => setActiveTab('settings')}
                     className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                       activeTab === 'settings' 
                         ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                         : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                     }`}
                   >
                     <Store className="w-5 h-5" />
                     Store Settings
                   </button>
                   <button
                     onClick={() => setActiveTab('payment')}
                     className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                       activeTab === 'payment' 
                         ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900' 
                         : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                     }`}
                   >
                     <CreditCard className="w-5 h-5" />
                     Payment Configs
                   </button>
                 </div>
               )}
            </div>

          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          {activeTab === 'tutorial' && <VendorTutorial />}
          {activeTab === 'products' && <ProductsManager vendorId={profile.uid} />}
          {activeTab === 'transactions' && <TransactionHistory vendorId={profile.uid} />}
          {activeTab === 'analytics' && <VendorAnalytics vendorId={profile.uid} />}
          {activeTab === 'messages' && <Messaging role="vendor" />}
          {activeTab === 'notifications' && <VendorNotifications vendorId={profile.uid} />}
          {activeTab === 'qa' && <QAManager vendorId={profile.uid} />}
          
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
                <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                  <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
                    <Store className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
                    Store Profile
                  </h2>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">
                    Update how your store appears to customers on the marketplace.
                  </p>
                </div>

                <form onSubmit={handleSaveSettings} className="p-8 space-y-6">
                  {message && (
                    <div className="p-4 rounded-xl text-sm font-medium border bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-100 dark:border-green-900/50">
                      {message}
                    </div>
                  )}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Store Name</label>
                      <input
                        type="text"
                        required
                        value={storeName}
                        onChange={(e) => setStoreName(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Store Description</label>
                      <textarea
                        required
                        rows={4}
                        value={storeDescription}
                        onChange={(e) => setStoreDescription(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Pickup Location</label>
                      <input
                        type="text"
                        placeholder="e.g., 123 Main St, City, State ZIP"
                        value={pickupLocation}
                        onChange={(e) => setPickupLocation(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                      />
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Customers will see this address to pick up their orders.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Store Policies</label>
                      <textarea
                        rows={4}
                        placeholder="Return policies, expected fulfillment times, shipping exceptions..."
                        value={policies}
                        onChange={(e) => setPolicies(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Contact Email (Optional)</label>
                        <input
                          type="email"
                          placeholder="e.g., hello@mystore.com"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Website URL (Optional)</label>
                        <input
                          type="url"
                          placeholder="https://mystore.com"
                          value={socialLinks.website}
                          onChange={(e) => setSocialLinks({ ...socialLinks, website: e.target.value })}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Instagram Username</label>
                        <input
                          type="text"
                          placeholder="mystore_ig"
                          value={socialLinks.instagram}
                          onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Facebook URL/Username</label>
                        <input
                          type="text"
                          placeholder="mystorefb"
                          value={socialLinks.facebook}
                          onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Twitter/X Username</label>
                        <input
                          type="text"
                          placeholder="mystore_x"
                          value={socialLinks.twitter}
                          onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Pickup Weekly Schedule</label>
                      <div className="space-y-3">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                          const schedule = pickupSchedules[index.toString()] || { enabled: false, startTime: '09:00', endTime: '17:00' };
                          return (
                            <div key={day} className="flex items-center gap-4 p-3 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-white dark:bg-neutral-900">
                              <div className="w-32 flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={schedule.enabled}
                                  onChange={(e) => {
                                    setPickupSchedules({
                                      ...pickupSchedules,
                                      [index.toString()]: { ...schedule, enabled: e.target.checked }
                                    });
                                  }}
                                  className="w-4 h-4 text-neutral-900 dark:text-white border-neutral-300 dark:border-neutral-600 rounded focus:ring-neutral-900 dark:focus:ring-white bg-white dark:bg-neutral-800"
                                />
                                <span className={`text-sm font-medium ${schedule.enabled ? 'text-neutral-900 dark:text-white' : 'text-neutral-400 dark:text-neutral-500'}`}>
                                  {day}
                                </span>
                              </div>
                              
                              {schedule.enabled ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <input
                                    type="time"
                                    value={schedule.startTime}
                                    onChange={(e) => {
                                      setPickupSchedules({
                                        ...pickupSchedules,
                                        [index.toString()]: { ...schedule, startTime: e.target.value }
                                      });
                                    }}
                                    className="px-3 py-1.5 border border-neutral-200 dark:border-neutral-700 rounded-lg focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white text-sm bg-white dark:bg-neutral-800 dark:text-white"
                                  />
                                  <span className="text-neutral-400 dark:text-neutral-500 text-sm">to</span>
                                  <input
                                    type="time"
                                    value={schedule.endTime}
                                    onChange={(e) => {
                                      setPickupSchedules({
                                        ...pickupSchedules,
                                        [index.toString()]: { ...schedule, endTime: e.target.value }
                                      });
                                    }}
                                    className="px-3 py-1.5 border border-neutral-200 dark:border-neutral-700 rounded-lg focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white text-sm bg-white dark:bg-neutral-800 dark:text-white"
                                  />
                                </div>
                              ) : (
                                <div className="flex-1 text-sm text-neutral-400 dark:text-neutral-500 italic">
                                  Closed
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Storefront Customization Section */}
                  <div className="pt-8 border-t border-neutral-100 dark:border-neutral-800 space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-neutral-900 dark:text-white font-serif">Storefront Customization</h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 font-light">Personalize how your storefront looks to customers.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Profile Picture URL</label>
                        <input
                          type="url"
                          placeholder="https://example.com/profile.jpg"
                          value={profilePicture}
                          onChange={(e) => setProfilePicture(e.target.value)}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Banner Type</label>
                        <select
                          value={bannerType}
                          onChange={(e) => setBannerType(e.target.value as any)}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        >
                          <option value="color">Solid Color</option>
                          <option value="gradient">Gradient</option>
                          <option value="image">Image</option>
                        </select>
                      </div>

                      {bannerType === 'image' && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Banner Image URL</label>
                          <input
                            type="url"
                            placeholder="https://example.com/banner.jpg"
                            value={bannerImage}
                            onChange={(e) => setBannerImage(e.target.value)}
                            className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                          />
                        </div>
                      )}

                      {(bannerType === 'color' || bannerType === 'gradient') && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                            {bannerType === 'color' ? 'Banner Color' : 'Banner Colors'}
                          </label>
                          <div className="flex gap-2">
                            {[0, 1, 2].map((i) => (
                              <div key={i} className="flex-1 flex gap-2">
                                <input
                                  type="color"
                                  value={bannerColors[i]?.startsWith('#') ? bannerColors[i] : '#f3f4f6'}
                                  onChange={(e) => {
                                    const newColors = [...bannerColors];
                                    newColors[i] = e.target.value;
                                    setBannerColors(newColors);
                                  }}
                                  className="h-11 w-16 p-1 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-neutral-50 dark:bg-neutral-800"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {bannerType === 'gradient' && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Gradient Style</label>
                          <select
                            value={gradientType}
                            onChange={(e) => setGradientType(e.target.value)}
                            className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                          >
                            <option value="linear-tb">Linear (Top to Bottom)</option>
                            <option value="linear-lr">Linear (Left to Right)</option>
                            <option value="linear-diag">Linear (Diagonal)</option>
                            <option value="radial">Radial Glow</option>
                            <option value="mesh">Soft Mesh</option>
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Theme Type</label>
                        <select
                          value={themeType}
                          onChange={(e) => setThemeType(e.target.value as any)}
                          className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-900 transition-colors text-sm dark:text-white"
                        >
                          <option value="solid">Solid Theme</option>
                          <option value="gradient">Gradient Theme</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Theme Colors</label>
                        <div className="flex gap-2">
                          {[0, 1, 2].map((i) => (
                            <div key={i} className="flex-1 flex gap-2">
                              <input
                                type="color"
                                value={themeColors[i]?.startsWith('#') ? themeColors[i] : '#171717'}
                                onChange={(e) => {
                                  const newColors = [...themeColors];
                                  newColors[i] = e.target.value;
                                  setThemeColors(newColors);
                                }}
                                className="h-11 w-16 p-1 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-neutral-50 dark:bg-neutral-800"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Preview Area */}
                    <div className="mt-6 p-6 border border-neutral-200 dark:border-neutral-800 rounded-2xl bg-neutral-50 dark:bg-neutral-900" style={{
                      background: themeType === 'gradient' 
                        ? `linear-gradient(135deg, ${themeColors.join(', ')})`
                        : themeColors[0]
                    }}>
                      <p className="text-xs font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-4">Preview</p>
                      <div className="relative h-32 rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-inner mb-4">
                        <div 
                          className="absolute inset-0 transition-all duration-500"
                          style={{
                            backgroundColor: bannerType === 'color' ? bannerColors[0] : undefined,
                            backgroundImage: bannerType === 'image' ? `url(${bannerImage})` : 
                                            bannerType === 'gradient' ? (
                                              gradientType === 'linear-tb' ? `linear-gradient(to bottom, ${bannerColors.join(', ')})` :
                                              gradientType === 'linear-lr' ? `linear-gradient(to right, ${bannerColors.join(', ')})` :
                                              gradientType === 'linear-diag' ? `linear-gradient(135deg, ${bannerColors.join(', ')})` :
                                              gradientType === 'radial' ? `radial-gradient(circle at center, ${bannerColors.join(', ')})` :
                                              `linear-gradient(45deg, ${bannerColors.join(', ')})`
                                            ) : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />
                        <div className="absolute -bottom-6 left-6 w-16 h-16 rounded-full border-4 border-white dark:border-neutral-950 shadow-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
                          {profilePicture ? (
                            <img src={profilePicture} alt="Vendor profile picture" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <Store className="w-6 h-6 text-neutral-400 dark:text-neutral-500" />
                          )}
                        </div>
                      </div>
                      <div className="pl-24">
                        <div className="h-4 w-32 bg-neutral-300 dark:bg-white/20 rounded mb-2 mix-blend-overlay" />
                        <div className="h-3 w-48 bg-neutral-200 dark:bg-white/10 rounded mix-blend-overlay" />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-8 py-3.5 rounded-xl text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            )}
            
            {activeTab === 'payment' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
                  <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                    <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
                      <CreditCard className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
                      Payments & Payouts
                    </h2>
                  </div>
                  <div className="p-8">
                  {profile.stripeOnboarded ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-900/50">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                        <span className="font-medium text-sm">Your Stripe account is connected and ready to receive payouts.</span>
                      </div>
                      {profile.stripeAccountId && (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                          Account ID: <code className="bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded text-neutral-900 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700">{profile.stripeAccountId}</code>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        To receive payouts for your sales, you need to connect a Stripe account.
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <button
                          onClick={handleConnectStripe}
                          className="bg-[#635BFF] text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-[#4B45C6] transition-colors shadow-sm"
                        >
                          Connect with Stripe
                        </button>
                        <button
                          onClick={handleMockConnectStripe}
                          className="bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors border border-neutral-200 dark:border-neutral-700"
                        >
                          Mock Connect (Testing)
                        </button>
                      </div>
                    </div>
                  )}

                  {profile.stripeAccountId && (
                    <div className="mt-8 pt-8 border-t border-neutral-100 dark:border-neutral-800">
                      <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-4">Account Management</h3>
                      <button
                        onClick={async () => {
                          if (confirm('Are you sure you want to disconnect your Stripe account? This will prevent you from receiving payouts until you reconnect.')) {
                            try {
                              await updateDoc(doc(db, 'users', auth.currentUser!.uid), {
                                stripeAccountId: null,
                                stripeOnboarded: false
                              });
                              useAuthStore.getState().fetchProfile(auth.currentUser!.uid);
                            } catch (err) {
                              console.error(err);
                            }
                          }
                        }}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
                      >
                        Disconnect Account & Switch Mode
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
